import { useCallback, useMemo, useState } from 'react';
import { GetStaticPaths, GetStaticPathsContext, GetStaticProps } from 'next';
import {
  Box,
  Container,
  Flex,
  Grid,
  GridItem,
  Heading,
  Text,
  VStack,
  Button,
  ButtonGroup,
  Alert,
  AlertIcon,
  AlertDescription,
  Icon,
} from '@chakra-ui/react';
import * as Sentry from '@sentry/nextjs';
import { useTranslation, Trans } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { Form } from 'react-final-form';
import createFocusOnErrorDecorator from 'final-form-focus';
import { useRouter } from 'next/router';
import { useSession } from 'next-auth/react';
import { NextSeo } from 'next-seo';
import dynamic from 'next/dynamic';
import { ArrowLeft } from '@phosphor-icons/react';
import Image from '@/lib/Image';
import { initializeApollo } from '@/lib/apolloClient';
import {
  GetPublicListingByPkDocument,
  GetPublicListingByPkQuery,
  GetPublicListingIdsDocument,
  GetPublicListingIdsQuery,
  GetPublicListingIdsQueryVariables,
  useSendOfferMutation,
} from '@/generated/graphql';
import ListingInfoCardHorizontal from '@/components/frontend/ListingInfoCardHorizontal';
import { ListingProps } from '@/types/listing';
import OfferForm from '@/components/frontend/OfferForm';
import OfferSchema, {
  makeInitialValues,
  OfferSchemaType,
} from '@/components/frontend/OfferForm/schema';
import useValidationSchema from '@/lib/hooks/useValidationSchema';
import { Complete } from '@/lib/types';
import PublicLayout from '@/components/frontend/PublicLayout';
import { urls, publicContainerAttachmentUrl } from '@/lib/urls';
import { listingImages } from '@/lib/listingUtils';
import userHasRole from '@/lib/utils/userHasRole';
import getRevalidateValue from '@/lib/getRevalidateValue';
import { fetchNavigations } from '@/lib/content-api';
import Link from '@/components/frontend/Link';
import { onAnalyticsFormEvent } from '@/lib/utils/analytics';
import { ForestDataLocales } from '@/types/forestData';
import { getListingForestryData, isValidLocale } from '@/lib/forestryData';
import ButtonLink from '@/components/frontend/ButtonLink';
import useSignOut from '@/lib/hooks/useSignOut';
import useToasts from '@/lib/toasts';
import fetchAPI from '@/lib/graphqlFetch';
import { SubmitButton } from '@/components/back-office/common/Form/Buttons';
import { getDataExceptionKey } from '@/lib/graphqlErrors';

const SCALogin = dynamic(() => import('@/components/frontend/SCALogin'), {
  ssr: false,
});

const focusOnError = createFocusOnErrorDecorator<OfferSchemaType>();

const LeaveOffer = ({ listing, forestData }: ListingProps) => {
  const { t } = useTranslation(['frontoffice', 'hasura_errors']);
  const {
    locale = 'fi',
    query: { signedIn },
  } = useRouter();
  const [pageState, setPageState] = useState<'FORM_FILLING' | 'THANK_YOU'>(
    'FORM_FILLING',
  );
  const { data: session } = useSession();
  const { coverImage } = listingImages(listing);
  const { errorToast } = useToasts();
  const signOut = useSignOut();
  const isIdentifiedPerson = userHasRole(session, ['identified_person']);
  const userName = session?.user.name;

  // Memoized validation, updates only if Schema changes
  const validate = useValidationSchema(
    OfferSchema(
      listing.sales_assignment?.price_type === 'minimum'
        ? (listing.total_price as number)
        : 0,
      listing.publish_end_datetime as string,
    ),
  );

  const [sendOffer] = useSendOfferMutation();

  const initialValues: Complete<Partial<OfferSchemaType>> = useMemo(
    () => makeInitialValues(session),
    [session],
  );

  const onSubmit = useCallback(
    async ({
      // we don't use offer_date and
      // offer_time in the public form
      offer_date,
      offer_time,
      first_name,
      last_name,
      offering_as_company, // used to toggle fields
      ...values
    }: OfferSchemaType) => {
      try {
        const res = await sendOffer({
          variables: {
            objects: {
              ...values,
              // don't include first name and last name if strongly auth'ed buyer
              // since they're set automatically from the token claim
              ...(!isIdentifiedPerson ? { first_name, last_name } : undefined),
              language: locale,
              listing_id: listing?.id || undefined,
            },
          },
        });

        if (!res.data) {
          throw new Error('No data returned from sendOffer');
        }

        onAnalyticsFormEvent('submission_success', { name: 'Ostotarjous' });
        setPageState('THANK_YOU');
      } catch (err) {
        onAnalyticsFormEvent('submission_failed', { name: 'Ostotarjous' });
        console.error('Submitting Offer failed: ', err);
        Sentry.captureException(err);

        // Default to a generic error message
        let errorDescription = t(
          'frontoffice:toasts.send_offer_failure',
          'Tarjousten lähettäminen epäonnistui, yritä uudelleen tai ota yhteyttä välittäjään.',
        );

        // Try to get a more specific error message from the caught error
        const exceptionKey = getDataExceptionKey(err);
        if (exceptionKey) {
          errorDescription = t(`hasura_errors:${exceptionKey}`);
        }

        errorToast({
          description: errorDescription,
        });
      }
    },
    [errorToast, isIdentifiedPerson, listing?.id, locale, sendOffer, t],
  );

  if (!listing) {
    return (
      <>
        <NextSeo title={t('leave_offer', 'Jätä tarjous')} />
        <Container p={5} maxW="container.2xl">
          {t('listing_not_found', 'Kohdetta ei löytynyt')}
        </Container>
      </>
    );
  }

  return (
    <>
      <NextSeo title={t('leave_offer', 'Jätä tarjous')} />
      <Flex flexDir="row" flexWrap="wrap">
        {coverImage?.attachment_public?.file_path && (
          <Box width="100%" height="300px" position="relative">
            <Image
              layout="fill"
              blurDataURL={
                coverImage.attachment_public?.blurdata_url || undefined
              }
              priority
              src={
                publicContainerAttachmentUrl(coverImage.attachment_public) || ''
              }
              alt={
                listing.name
                  ? t('cover_image_for', 'Kansikuva kohteelle {{ name }}', {
                      name: listing.name,
                    })
                  : t('cover_image', 'Kansikuva')
              }
            />
          </Box>
        )}
        <ListingInfoCardHorizontal listing={listing} forestData={forestData} />
      </Flex>

      <Box bg="white" py={5}>
        <Container maxW="container.2xl" py={0} pb={10}>
          <Grid templateColumns="repeat(7, 1fr)" gap={6} py={5}>
            <GridItem colSpan={[7, 7, 2]}>
              <Heading size="lg">{t('leave_offer', 'Jätä tarjous')}</Heading>
            </GridItem>
            {listing.require_strong_customer_authentication &&
            !isIdentifiedPerson ? (
              <GridItem colSpan={[7, 7, 4, 3]}>
                <Heading size="lg" mb={3}>
                  {t('electronic_identification', 'Sähköinen tunnistus')}
                </Heading>
                <VStack align="left" shouldWrapChildren gap={6}>
                  <Text>
                    {t(
                      'listing_requires_strong_customer_auth',
                      'Tarjouksen jättäminen vaatii vahvan tunnistautumisen. Voit tunnistautua esim. pankkitunnuksillasi, painamalla alla olevaa nappia.',
                    )}
                  </Text>
                  {process.env.NEXT_PUBLIC_MAINTENANCE_BREAK === 'true' ? (
                    <Alert status="info">
                      <AlertIcon />
                      {t(
                        'maintenance_break_info_offer',
                        'Teemme huoltotöitä palvelussa. Tarjouksen teko ei väliaikaisesti ole mahdollista. Ota yhteyttä välittäjään tehdäksesi tarjouksen.',
                      )}
                    </Alert>
                  ) : null}
                  <SCALogin />
                  <Link
                    color="primary.500"
                    fontWeight="bold"
                    href={urls.public.listing.get(
                      `${listing?.public_id || ''}`,
                    )}
                    display="flex"
                    alignItems="center"
                    width="max-content"
                  >
                    <Icon as={ArrowLeft} mr={2} weight="bold" />{' '}
                    {t('return_to_listing_page', 'Palaa kohdesivulle')}
                  </Link>
                </VStack>
              </GridItem>
            ) : (
              <GridItem colSpan={[7, 7, 4, 3]}>
                {pageState === 'FORM_FILLING' && (
                  <VStack spacing={10} alignItems="flex-start">
                    {isIdentifiedPerson ? (
                      <Alert status="success" background="highlightBackground">
                        <AlertIcon />
                        <AlertDescription>
                          <Trans t={t} i18nKey="identified_user_signed_in">
                            <Text>
                              Olet kirjautuneena <strong>{{ userName }}</strong>
                              .
                            </Text>
                            <Text>
                              Henkilötietosi syötetään automaattisesti
                              lomakkeeseen.
                            </Text>
                          </Trans>
                          {signedIn ? (
                            <Text mt={2}>
                              {t(
                                'identified_user_sign_out_info',
                                'Voit kirjautua ulos ikkunan alalaidassa olevasta linkistä.',
                              )}
                            </Text>
                          ) : null}
                        </AlertDescription>
                      </Alert>
                    ) : null}
                    <Text whiteSpace="pre-line" fontSize="xl">
                      <Trans t={t} i18nKey="offer_description">
                        Täytä tarjous huolella. Myyjä varaa oikeuden hyväksyä
                        tai olla hyväksymättä minkä tahansa tarjouksen. Tutustu{' '}
                        <Link
                          href={
                            {
                              sv: '/regler-for-anbudsforfarande',
                              en: '/rules-for-the-tender-procedure',
                            }[locale] || '/tarjousmenettelyn-saannot'
                          }
                          target="_blank"
                          color="highlightColor"
                        >
                          Tarjousmenettelyn ehtoihin
                        </Link>
                        .
                      </Trans>
                    </Text>

                    {process.env.NEXT_PUBLIC_MAINTENANCE_BREAK === 'true' ? (
                      <Alert status="info">
                        <AlertIcon />
                        {t(
                          'maintenance_break_info_offer',
                          'Teemme huoltotöitä palvelussa. Tarjouksen teko ei väliaikaisesti ole mahdollista. Ota yhteyttä välittäjään tehdäksesi tarjouksen.',
                        )}
                      </Alert>
                    ) : null}

                    <Form
                      initialValues={initialValues}
                      onSubmit={onSubmit}
                      validate={validate}
                      decorators={[focusOnError]}
                      subscription={{
                        submitting: true,
                        values: false,
                        error: true,
                      }}
                      render={(props) => (
                        <>
                          <OfferForm
                            {...props}
                            listing={listing}
                            disableNameFields={userHasRole(session, [
                              'identified_person',
                            ])}
                          />
                          <Flex justifyContent="flex-end" width="100%">
                            <ButtonLink
                              variant="ghost"
                              boxShadow="md"
                              size="lg"
                              borderRadius="full"
                              colorScheme="success"
                              link={{
                                title: t('cancel', 'Peruuta'),
                                url: urls.public.listing.get(
                                  listing.public_id ?? '',
                                ),
                                mt: 4,
                                mr: 2,
                              }}
                            />
                            <SubmitButton
                              size="lg"
                              mt={4}
                              boxShadow="md"
                              form="offer-form"
                            >
                              {t('send_offer', 'Lähetä tarjous')}
                            </SubmitButton>
                          </Flex>
                        </>
                      )}
                    />
                  </VStack>
                )}
                {pageState === 'THANK_YOU' && (
                  <Box mb="10" flexDir="column">
                    <Heading as="h2" mb="5">
                      {t(
                        'offer_sent_successfully',
                        'Tarjous lähetetty onnistuneesti',
                      )}
                    </Heading>
                    <Text mb="5">
                      <Trans t={t} i18nKey="send_new_offer">
                        Mikäli haluat lähettää uuden tarjouksen, voit{' '}
                        <Button
                          variant="link"
                          colorScheme="primary"
                          fontSize="inherit"
                          onClick={() => setPageState('FORM_FILLING')}
                        >
                          palauttaa lomakkeen alkutilaan
                        </Button>
                        .
                      </Trans>
                    </Text>
                    {isIdentifiedPerson ? (
                      <Text mb="5">
                        <Trans t={t} i18nKey="sign_out_after_offer">
                          Voit jatkaa sivuston käyttöä kirjautuneena, tai{' '}
                          <Button
                            variant="link"
                            colorScheme="primary"
                            fontSize="inherit"
                            onClick={() => signOut({ redirect: false })}
                          >
                            kirjautua ulos
                          </Button>
                          .
                        </Trans>
                      </Text>
                    ) : null}
                    <ButtonGroup mt={10}>
                      <ButtonLink
                        m={0}
                        size="lg"
                        colorScheme="primary"
                        link={{
                          title: t(
                            'return_to_listing_page',
                            'Palaa kohdesivulle',
                          ),
                          url: urls.public.listing.get(
                            `${listing?.public_id || ''}`,
                          ),
                        }}
                      />
                      <ButtonLink
                        m={0}
                        size="lg"
                        colorScheme="primary"
                        link={{
                          title: t('new_search', 'Tee uusi kohdehaku'),
                          url: urls.public.buy.index,
                        }}
                      />
                    </ButtonGroup>
                  </Box>
                )}
              </GridItem>
            )}

            <GridItem h="20" colSpan={[7, 7, 2]} />
          </Grid>
        </Container>
      </Box>
    </>
  );
};

LeaveOffer.layout = PublicLayout;

export default LeaveOffer;

export const getStaticPaths: GetStaticPaths = async ({
  locales = ['fi'],
}: GetStaticPathsContext) => {
  try {
    if (process.env.BUILD_STATIC_LISTING_PAGES !== 'true') {
      throw new Error(
        'BUILD_STATIC_LISTING_PAGES is not "true". Not generating static listing pages.',
      );
    }

    const publicListings = await fetchAPI<
      GetPublicListingIdsQuery,
      GetPublicListingIdsQueryVariables
    >(
      GetPublicListingIdsDocument,
      {},
      {
        'content-type': 'application/json',
        'x-hasura-admin-secret': process.env.HASURA_GRAPHQL_ADMIN_SECRET || '',
      },
    );

    return {
      paths: locales.flatMap((locale) =>
        publicListings.listing_public
          .filter((listing) => !!listing.public_id)
          .map((listing) => ({
            params: {
              listing: listing.public_id || '',
            },
            locale,
          })),
      ),
      fallback: 'blocking',
    };
  } catch (err) {
    console.error(err);
    Sentry.captureException(err);
    return {
      paths: [],
      fallback: 'blocking',
    };
  }
};

export const getStaticProps: GetStaticProps = async (context) => {
  const client = initializeApollo();
  const { params: { listing: id } = {}, locale = 'fi' } = context;

  // stale while revalidate strategy:
  // - serve 404 if we can't get the listing
  // - use stale if anything else fails

  try {
    const { data, errors } = await client.query<GetPublicListingByPkQuery>({
      query: GetPublicListingByPkDocument,
      fetchPolicy: 'network-only',
      variables: { public_id: id, where: { is_public: { _eq: true } } },
      context: {
        headers: {
          'content-type': 'application/json',
          'x-hasura-admin-secret': process.env.HASURA_GRAPHQL_ADMIN_SECRET,
        },
      },
    });

    if (!data || !data.listing_public.length) {
      if (errors?.length) {
        console.error('Error getting listing for Offer page', errors);
        Sentry.captureException(errors.toString());
      }
      // We couldn't get the listing, so there's a chance it has
      // expired. Let's return 404 with a short revalidate in case
      // it's only a temporary outage.
      return {
        notFound: true,
        revalidate: getRevalidateValue(30),
      };
    }

    const [listing] = data.listing_public;

    const forestData = await getListingForestryData(
      listing,
      isValidLocale(locale) ? locale : ForestDataLocales.fi,
    );

    const navigations = await fetchNavigations({
      lang: locale,
    });

    // Pass post data to the page via props
    return {
      props: {
        ...(await serverSideTranslations(locale || 'fi', [
          'frontoffice',
          'backoffice',
          'hasura_errors',
        ])),
        initialApolloState: client.cache.extract(),
        navigations: navigations || null,
        listing: listing || null,
        forestData,
      },
      revalidate: getRevalidateValue(),
    };
  } catch (err) {
    console.error('Error while getting props for Offer page: ', err);
    Sentry.captureException(err);
    throw new Error(
      `Offer page for ${JSON.stringify(
        context.params?.listing,
      )} stale while no data`,
    );
  }
};
